import type { RefObject } from 'react';
import { useContext } from 'react';
import type { GeoChartMeta, GeoDataType, GeoLayerCoordinates, Point, ProfileWellExtended, WellTree } from './types';
import { DEFAULT_MAX_DEPTH, DEFAULT_MIN_DEPTH, DEFAULT_SCALE, DEPTH_OFFSET } from './base';
import { toSvg } from 'html-to-image';
import type { ErrorData, GeoData, Horizon, Permission, SeamGeoData, WellGeoData } from '@/store/geo-test/types';
import * as XLSX from 'xlsx';
import type { Fgi } from '@/api/api.v2';
import { useSelector } from 'react-redux';
import type { RootState } from '../WellLoggingOperations/types';
import { GeoDataContext } from './GeoTestPage';

// Делит кубическую кривую Безъе на 2 части относительно параметра t, который имеет значение от 0 до 1.
// Где 0 это начало кривой, а 1 это конец. Соответсвенно 0.5 это середина.
// На вход получает 4 координаты исходной кривой и возвращает координаты кривых.
// fist, где параметр проходит от 0 до t. second от t до 1

export const splitBezier = (P0: Point, P1: Point, P2: Point, P3: Point, t: number) => {
    const Q0 = {
        x: (1 - t) * P0.x + t * P1.x,
        y: (1 - t) * P0.y + t * P1.y,
    };
    const Q1 = {
        x: (1 - t) * P1.x + t * P2.x,
        y: (1 - t) * P1.y + t * P2.y,
    };
    const Q2 = {
        x: (1 - t) * P2.x + t * P3.x,
        y: (1 - t) * P2.y + t * P3.y,
    };

    const R0 = {
        x: (1 - t) * Q0.x + t * Q1.x,
        y: (1 - t) * Q0.y + t * Q1.y,
    };
    const R1 = {
        x: (1 - t) * Q1.x + t * Q2.x,
        y: (1 - t) * Q1.y + t * Q2.y,
    };

    const S = {
        x: (1 - t) * R0.x + t * R1.x,
        y: (1 - t) * R0.y + t * R1.y,
    };

    return { first: { P0, P1: Q0, P2: R0, P3: S }, second: { P0: S, P1: R1, P2: Q2, P3 } };
};

// пропласток определяется 6 координатами
// startX - левые точки по горизонтальной оси,
// startTop - верхняя левая точка по вертикальной оси, startBottom - нижняя левая точка по вертикальной оси,
// endX - правые точки по горизонтальной оси,
// endTop- верхняя правая точка по вертикальной оси, endBottom - нижняя правая точка по вертикальной оси
// функция выдает path для пропластка который по горизонтальной оси повторяет входной пропласток в точности до t
// где 0, выдаст пустой пропласток, 1 выдаст исходный, а 0.5 точно половину
// геометрически этой отсечение вертикальным срезом исходного пропластка

export const getHalfPath = (
    { startX, startTop, startBottom, endX, endTop, endBottom }: GeoLayerCoordinates,
    smoothCut = true,
    t = 0.5,
) => {
    const centerX = (startX + endX) / 2;

    const { first: topLine } = splitBezier(
        { x: startX, y: startTop },
        { x: centerX, y: startTop },
        { x: centerX, y: endTop },
        { x: endX, y: endTop },
        t,
    );
    const { second: bottomLine } = splitBezier(
        { x: endX, y: endBottom },
        { x: centerX, y: endBottom },
        { x: centerX, y: startBottom },
        { x: startX, y: startBottom },
        1 - t,
    );

    return `
        M ${topLine.P0.x} ${topLine.P0.y}
        C ${topLine.P1.x} ${topLine.P1.y} ${topLine.P2.x} ${topLine.P2.y} ${topLine.P3.x} ${
            smoothCut ? (topLine.P3.y + bottomLine.P0.y) / 2 : topLine.P3.y
        }
        L ${bottomLine.P0.x} ${smoothCut ? (topLine.P3.y + bottomLine.P0.y) / 2 : bottomLine.P0.y}
        C ${bottomLine.P1.x} ${bottomLine.P1.y} ${bottomLine.P2.x} ${bottomLine.P2.y} ${bottomLine.P3.x} ${
            bottomLine.P3.y
        }
        Z
    `;
};

//возврашает значение параметра d для пропластка по его координатам

export const getPath = ({ startX, startTop, startBottom, endX, endTop, endBottom }: GeoLayerCoordinates) => {
    const centerX = (startX + endX) / 2;
    return `
        M ${startX} ${startTop}
        C ${centerX} ${startTop} ${centerX} ${endTop} ${endX} ${endTop}
        L ${endX} ${endBottom}
        C ${centerX} ${endBottom} ${centerX} ${startBottom} ${startX} ${startBottom}
        Z
    `;
};

//средний перепад высот между скважинами

export const hasIntersection = (a: { start: number; end: number }, b: { start: number; end: number }): boolean => {
    return a.start < b.end && b.start < a.end;
};

export const meanStratumsDiff = (
    well: WellGeoData,
    nextWell: WellGeoData,
    currentStratum: { start: number; end: number; key: number },
): { top: number; bottom: number } => {
    const connectedStratums = well.stratums.filter(s => {
        return s.key !== currentStratum.key && nextWell.stratums.some(ns => s.key === ns.key);
    });

    const upperCandidates = connectedStratums.filter(s => s.end <= currentStratum.start);
    const lowerCandidates = connectedStratums.filter(s => s.start >= currentStratum.end);

    const upper = upperCandidates.sort((a, b) => b.end - a.end)[0];
    const lower = lowerCandidates.sort((a, b) => a.start - b.start)[0];

    const upperNext = upper && nextWell.stratums.find(s => s.key === upper.key);
    const lowerNext = lower && nextWell.stratums.find(s => s.key === lower.key);

    if (upper && lower && upperNext && lowerNext) {
        const top = upper.end;
        const bottom = lower.start;

        const percentStart = (currentStratum.start - top) / (bottom - top);
        const percentEnd = (currentStratum.end - top) / (bottom - top);

        const topNext = upperNext.end;
        const bottomNext = lowerNext.start;

        const mappedStart = topNext + (bottomNext - topNext) * percentStart;
        const mappedEnd = topNext + (bottomNext - topNext) * percentEnd;

        return { top: mappedStart, bottom: mappedEnd };
    }

    if (upper && upperNext) {
        const delta = upperNext.end - upper.end;
        return { top: currentStratum.start + delta, bottom: currentStratum.end + delta };
    }

    if (lower && lowerNext) {
        const delta = lower.start - currentStratum.start;
        return { top: currentStratum.start - delta, bottom: currentStratum.end - delta };
    }

    return { top: currentStratum.start, bottom: currentStratum.end };
};

// получает координаты пропластка для соседней скважины, для случая если пропласток по всоте идет пропорционально основному пласту
// на вход подается well - левая скважина, nextWell - правая скважина,
// order - 'current' - если мы вводим координаты пропластка на well и нужно получить координаты для nextWell
// 'next' - ситуация наоборот

export const getRelatedCoordinates = (
    seam: SeamGeoData,
    well: WellGeoData,
    nextWell: WellGeoData,
    order: 'current' | 'next',
) => {
    let stratum = well.stratums.find(({ key }) => Number(seam.key.split('_')[0]) === key);
    let nextStratum = nextWell.stratums.find(({ key }) => Number(seam.key.split('_')[0]) === key);
    if (
        ((order === 'current' && stratum) || (order === 'next' && nextStratum)) &&
        !hasIntersection(order === 'current' ? stratum : nextStratum, seam)
    ) {
        if (order === 'next') {
            return meanStratumsDiff(nextWell, well, { ...seam, key: 0 });
        } else {
            return meanStratumsDiff(well, nextWell, { ...seam, key: 0 });
        }
    }

    if (nextStratum && !stratum) {
        const rel = meanStratumsDiff(nextWell, well, nextStratum);
        stratum = { ...nextStratum, end: rel.bottom, start: rel.top };
    }
    if (!nextStratum && stratum) {
        const rel = meanStratumsDiff(well, nextWell, stratum);
        nextStratum = { ...stratum, end: rel.bottom, start: rel.top };
    }

    if (!stratum || !nextStratum) {
        throw Error(`Cannot find stratum for seam - ${seam.key}`);
    }

    const stratumHeigh = stratum.end - stratum.start;
    const nextStratumHeigh = nextStratum.end - nextStratum.start;

    return order === 'current'
        ? {
              bottom: ((seam.end - stratum.start) * nextStratumHeigh) / stratumHeigh + nextStratum.start,
              top: ((seam.start - stratum.start) * nextStratumHeigh) / stratumHeigh + nextStratum.start,
          }
        : {
              bottom: ((seam.end - nextStratum.start) * stratumHeigh) / nextStratumHeigh + stratum.start,
              top: ((seam.start - nextStratum.start) * stratumHeigh) / nextStratumHeigh + stratum.start,
          };
};

// переводит метрические координаты в пискельные

export const getCoordinates = (x: number, bottom: number, top: number, meta: GeoChartMeta) => {
    if (!meta.picture) {
        throw new Error('picture is missing in GeoChartBody');
    }

    const { distanceToPixelsX } = meta.picture;
    const { depthToPixelsY } = meta.depths;

    return {
        x: distanceToPixelsX(x),
        bottom: depthToPixelsY(bottom),
        top: depthToPixelsY(top),
    };
};

type WithId = { id: number } | { perfId: number };

export const getSelectedEntities = <T extends WithId, R extends Record<number, T[]>>(
    entities: R,
    selectedEntities: Record<number, number[]>,
): R => {
    const result = {} as R;

    for (const [keyStr, items] of Object.entries(entities)) {
        const key = Number(keyStr);
        const selectedIds = new Set(selectedEntities[key] ?? []);

        result[key] = items.filter(item => {
            const entityId: number = 'id' in item ? item.id : 'perfId' in item ? item.perfId : -1;
            return selectedIds.has(entityId);
        }) as R[typeof key];
    }

    return result;
};

// const downloadMergedSvg = (
//     svgHeaderRef: RefObject<SVGSVGElement>,
//     svgBodyRef: RefObject<SVGSVGElement>,
//     start = 1450,
//     end = 3000,
// ) => {
//     const svgHeader = svgHeaderRef.current;
//     const svgBody = svgBodyRef.current;

//     if (!svgHeader || !svgBody) return;

//     const headerRect = svgHeader.getBoundingClientRect();
//     const bodyRect = svgBody.getBoundingClientRect();

//     const svgHeaderContent = svgHeader.innerHTML;
//     const svgBodyContent = svgBody.innerHTML;

//     const offsetX = bodyRect.left - headerRect.left;
//     const offsetY = headerRect.bottom - headerRect.top;

//     const clipPathId = 'clip-body';
//     const clipHeight = end - start;

//     const width = Math.max(headerRect.width, offsetX + bodyRect.width);
//     const height = Math.max(headerRect.height, offsetY + clipHeight);

//     const mergedSvg = `
// <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
//   <defs>
//     <clipPath id="${clipPathId}">
//       <rect x="0" y="${start}" width="${width}" height="${clipHeight}" />
//     </clipPath>
//   </defs>
//   <g>${svgHeaderContent}</g>
//   <g transform="translate(${offsetX}, ${offsetY - start})" clip-path="url(#${clipPathId})">
//     ${svgBodyContent}
//   </g>
// </svg>`.trim();

//     const blob = new Blob([mergedSvg], { type: 'image/svg+xml;charset=utf-8' });
//     const url = URL.createObjectURL(blob);
//     const a = document.createElement('a');
//     a.href = url;
//     a.download = 'merged.svg';
//     document.body.appendChild(a);
//     a.click();
//     document.body.removeChild(a);
//     URL.revokeObjectURL(url);
// };

export const getMinMaxDepth = (geoData: GeoData): [number, number] => {
    let minDepth: number | undefined = undefined;
    let maxDepth: number | undefined = undefined;

    const processDepth = (depth: number) => {
        minDepth = minDepth != undefined ? Math.min(minDepth, depth) : depth;
        maxDepth = maxDepth != undefined ? Math.max(maxDepth, depth) : depth;
    };

    for (const stratum of geoData?.stratums ?? []) {
        processDepth(stratum.start);
        processDepth(stratum.end);
    }

    geoData.wells.forEach(well => {
        if (well.depth) processDepth(well.depth);
        well.stratums.forEach(stratum => {
            processDepth(stratum.start);
            processDepth(stratum.end);
        });
        well.seams.forEach(stratum => {
            processDepth(stratum.start);
            processDepth(stratum.end);
        });
    });

    if (geoData?.stratigraphy)
        for (const val of Object.values(geoData?.stratigraphy)) {
            for (const obj of val) {
                processDepth(obj.start);
                processDepth(obj.end);
            }
        }

    if (minDepth === undefined || maxDepth === undefined) {
        minDepth = DEFAULT_MIN_DEPTH;
        maxDepth = DEFAULT_MAX_DEPTH;
    } else {
        minDepth = minDepth - DEPTH_OFFSET;
        maxDepth = Number(maxDepth) + DEPTH_OFFSET;
    }
    return [minDepth, maxDepth];
};

const createDivCrop = (element: HTMLDivElement, start: number, end: number) => {
    const cropWidth = element.offsetWidth;
    const cropHeight = end - start;

    const wrapper = document.createElement('div');
    wrapper.style.position = 'static';
    wrapper.style.width = `${cropWidth}px`;
    wrapper.style.height = `${cropHeight}px`;
    wrapper.style.overflow = 'hidden';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.background = 'white';
    wrapper.style.zIndex = '-1';

    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.transform = `translateY(-${start}px)`;
    clone.style.margin = '0';
    clone.style.padding = '0';

    wrapper.appendChild(clone);
    return wrapper;
};

export const downloadSvg = async (
    divHeaderRef: RefObject<HTMLDivElement>,
    divBodyRef: RefObject<HTMLDivElement>,
    start: number,
    end: number,
    title: string,
) => {
    const divHeader = divHeaderRef.current;
    const divBody = divBodyRef.current;
    if (!divHeader || !divBody) {
        return;
    }
    const wrapper = createDivCrop(divBody, start / DEFAULT_SCALE, end / DEFAULT_SCALE);
    const merged = document.createElement('div');
    merged.style.top = '-30000px';
    merged.style.width = 'fit-content';
    const clonedHeader = divHeader.cloneNode(true) as HTMLDivElement;
    clonedHeader.style.overflow = 'hidden';
    merged.appendChild(clonedHeader);
    merged.appendChild(wrapper.cloneNode(true));
    document.body.appendChild(merged);

    const dataUrl = await toSvg(merged);

    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = title;
    a.click();
    document.body.removeChild(merged);
};

export const assignUniqueStratumAndSeamKeys = (geoData: GeoData, selectedHorizon: Horizon[]) => {
    if (geoData) {
        if (!geoData.horizons) {
            geoData.horizons = selectedHorizon
                .map((horizon: Horizon, i: number) => {
                    if (selectedHorizon[i].checked) {
                        return horizon;
                    }
                    return null;
                })
                .filter(horizon => horizon !== null);
        }
    }

    return geoData;
};

const distanceWell = (w1: WellGeoData, w2: WellGeoData): number => {
    return Math.sqrt((w1.x - w2.x) ** 2 + (w1.y - w2.y) ** 2);
};

export const exportToExcel = (data: ErrorData[], fileName = 'Пропущенные данные модуля геопрофилей.xlsx') => {
    if (data.length === 0) return;

    const headers = {
        entityName: 'Объект',
        wellName: 'Скважина',
        name: 'Название',
        missingProps: 'Пропущенные поля',
    };

    const sheetData = [
        Object.values(headers),
        ...data.map(({ entityName, wellName, name, missingProps }) => [
            entityName,
            wellName || '',
            name,
            missingProps.join(', '),
        ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Errors');

    worksheet['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 10 }, { wch: 20 }];

    XLSX.writeFile(workbook, fileName);
};

export const validateEntityProps = <T extends { name?: string; key?: string | number }>(
    entity: T,
    entityName: [string, string],
    requiredProps: Array<keyof T>,
    wellName?: string,
): ErrorData | void => {
    const missingProps = requiredProps.filter(prop => entity[prop] == null);
    const name = entity.name ?? entity.key?.toString() ?? '';
    const errorString = `У ${entityName[1]} ${wellName ? `скважины ${wellName}` : ''} ${name} не ${
        missingProps.length === 1 ? 'задано следующее свойство' : 'заданы следующие свойства'
    }: ${missingProps.join(', ')}`;

    if (missingProps.length > 0) {
        return {
            entityName: entityName[0],
            wellName,
            name,
            missingProps: missingProps as string[],
            errorString,
        };
    }
};

export const checkGeoData = (geoData: GeoData | null): ErrorData[] => {
    const res: ErrorData[] = [];

    if (!geoData || !geoData.wells) {
        return res;
    }

    for (const well of geoData.wells) {
        const wellError = validateEntityProps(well, ['скважина', 'скважины'], ['x', 'y']);

        if (wellError) {
            res.push(wellError);
        }

        for (const stratum of well.stratums) {
            const stratumError = validateEntityProps(stratum, ['пласт', 'пласта'], ['start', 'end'], well.name);

            if (stratumError) {
                res.push(stratumError);
            }
        }

        for (const seam of well.seams) {
            const seamError = validateEntityProps(seam, ['пропласток', 'пропластка'], ['start', 'end'], well.name);

            if (seamError) {
                res.push(seamError);
            }
        }
    }

    return res;
};

export const prepareGeoData = (geoData: GeoData): GeoData => {
    if (!geoData?.wells) {
        return geoData;
    }

    const newWells: WellGeoData[] = [];
    let prevWell = null;
    for (const well of geoData.wells.sort(({ x: x1 }, { x: x2 }) => x1 - x2)) {
        if (!prevWell) {
            newWells.push(well);
            prevWell = well;
        } else {
            newWells.push({ ...well, x: prevWell.x + distanceWell(well, prevWell) });
            prevWell = { ...well, x: prevWell.x + distanceWell(well, prevWell) };
        }
    }

    return {
        ...geoData,
        wells: newWells.filter(({ isBadVersion }) => !isBadVersion),
        stratums: geoData?.wells[0].stratums ?? geoData.stratums,
    };
};

export const getObjectWithMinPriorityAndLatestVersionDate = (fgiList: Fgi[]) => {
    const minPriority = Math.min(...fgiList.map(obj => obj.priority));

    const filteredByMinPriority = fgiList.filter(obj => obj.priority === minPriority);

    return filteredByMinPriority.reduce((latest, current) => {
        return new Date(current.versionDate) > new Date(latest.versionDate) ? current : latest;
    });
};

export const useGeoData = (): GeoDataType => {
    const context = useContext(GeoDataContext);

    const { geoData, perforationData, testingData, selectedPerforations, selectedTestings } = useSelector(
        (store: RootState) => store.geoTest,
    );
    if (!geoData || !perforationData || !context) {
        throw new Error('useGeoData может быть использован только с загруженными данными');
    }
    const { divColumnsHeaderRef, divColumnsBodyRef, svgBodyRef } = context;

    return {
        geoData: prepareGeoData(geoData),
        perforationData,
        selectedPerforations,
        selectedTestings,
        testingData,
        divColumnsBodyRef,
        divColumnsHeaderRef,
        svgBodyRef,
    };
};

export const useActiveGroupWellIds = () => {
    const lists = useSelector((state: RootState) => state.lists);
    if ((lists?.activeGroupKey || lists.activeItemKey) && lists.data) {
        const activeGroup = (lists.data ?? []).filter(
            item => item.key === lists.activeGroupKey || item.key === lists.activeItemKey,
        )[0];
        if (activeGroup && activeGroup.node === 'group') {
            const groupIds = activeGroup.children.map(child => child.id);
            if (groupIds && groupIds.length > 0) {
                return groupIds;
            }
        }
    }
};

export const toPermission = (isPrivate: boolean, readOnly: boolean): Permission => {
    if (isPrivate) {
        return 'private';
    } else if (readOnly) {
        return 'readonly';
    }
    return 'general';
};

export const groupWellsToTree = (wells: ProfileWellExtended[]): WellTree[] => {
    const result: WellTree[] = [];

    let currentField: WellTree | null = null;
    let currentKupol: WellTree['elevations'][0] | null = null;

    for (const well of wells) {
        if (!currentField || currentField.fieldName !== well.fieldName) {
            currentField = {
                fieldName: well.fieldName,
                elevations: [],
            };
            result.push(currentField);
            currentKupol = null;
        }

        if (!currentKupol || currentKupol.kupolName !== well.kupolName) {
            currentKupol = {
                kupolName: well.kupolName,
                wells: [],
            };
            currentField.elevations.push(currentKupol);
        }

        currentKupol.wells.push(well);
    }

    return result;
};
